import React, { useEffect, useState, useRef } from 'react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import {
    listDocs,
    createDoc,
    createSubDoc,
    listSubcollection,
    updateSubDoc,
    deleteSubDoc,
    updateDocById,
} from '../services/dbService';
import ImageUpload from '../components/ImageUpload';
import toast from 'react-hot-toast';
import './Events.css';

const Events = () => {
    const [events, setEvents] = useState([]); // aggregated subEvents
    const [categories, setCategories] = useState([]); // eventTypes
    const [servicesAvailable, setServicesAvailable] = useState([]); // all services we can attach
    const [selectedServiceIds, setSelectedServiceIds] = useState([]); // service ids selected for current subEvent
    const [showAddService, setShowAddService] = useState(false);
    const [creatingService, setCreatingService] = useState(false);
    const [newServiceData, setNewServiceData] = useState({ name: '', description: '', price: '' });
    const serviceImageRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventTypeId: '',
        imageUrl: '',
        isAvailable: true,
    });
    const imageUploadRef = useRef(null);

    useEffect(() => {
        fetchEvents();
        fetchCategories();
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const docs = await listDocs('services');
            setServicesAvailable(docs.map((d) => ({ id: d.id, ...d })));
        } catch (err) {
            console.error('Failed to fetch services:', err);
            toast.error('Failed to fetch services');
        }
    };

    // Fetch all subEvents across all eventTypes
    const fetchEvents = async () => {
        try {
            setLoading(true);
            const types = await listDocs('eventtypes');
            const all = [];
            for (const t of types) {
                const subs = await listSubcollection('eventtypes', t.id, 'subevents');
                for (const s of subs) {
                    // Normalize fields to expected schema
                    const normalizedServices = (s.services || s.service || []).map((svc) => ({
                        id: svc.id || svc._id || null,
                        name: svc.name || svc.title || '',
                        description: svc.description || svc.desc || '',
                        price: typeof svc.price === 'number' ? svc.price : parseFloat(svc.price || svc.basePrice || 0) || 0,
                        imageUrl: svc.imageUrl || svc.image || null,
                    }));

                    const basePrice = typeof s.basePrice === 'number' ? s.basePrice : parseFloat(s.basePrice || s.price || 0) || 0;
                    const rating = typeof s.rating === 'number' ? s.rating : parseFloat(s.rating) || 0;
                    const reviewCount = typeof s.reviewCount === 'number' ? s.reviewCount : parseInt(s.reviewCount) || 0;

                    all.push({
                        id: s.id,
                        eventTypeId: t.id,
                        eventTypeTitle: t.title || t.name || '',
                        title: s.title || s.name || '',
                        description: s.description || s.desc || '',
                        imageUrl: s.imageUrl || s.image || null,
                        basePrice,
                        isAvailable: typeof s.isAvailable === 'boolean' ? s.isAvailable : true,
                        rating,
                        reviewCount,
                        services: normalizedServices,
                        createdAt: s.created_at || s.createdAt || null,
                    });
                }
            }
            setEvents(all);
        } catch (err) {
            console.error('Fetch events error:', err);
            toast.error('Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const docs = await listDocs('eventtypes');
            setCategories(docs.map((d) => ({ id: d.id, name: d.title || d.name || '' })));
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch categories');
        }
    };

    const handleOpenModal = (event = null) => {
        if (event) {
            setEditingEvent(event);
            setFormData({
                title: event.title || '',
                description: event.description || '',
                eventTypeId: event.eventTypeId || '',
                imageUrl: event.imageUrl || '',
                isAvailable: typeof event.isAvailable === 'boolean' ? event.isAvailable : true,
            });
            // Prefill selected services if the subEvent already contains a services array
            if (Array.isArray(event.services) && event.services.length > 0) {
                setSelectedServiceIds(event.services.map((s) => s.id).filter(Boolean));
            } else {
                setSelectedServiceIds([]);
            }
        } else {
            setEditingEvent(null);
            setFormData({
                title: '',
                description: '',
                eventTypeId: '',
                imageUrl: '',
                isAvailable: true,
            });
            setSelectedServiceIds([]);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingEvent(null);
        setFormData({
            title: '',
            description: '',
            eventTypeId: '',
            imageUrl: '',
            isAvailable: true,
        });
        setSelectedServiceIds([]);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Ensure any pending image file is uploaded before saving
            let imageUrl = formData.imageUrl;
            if (imageUploadRef.current && imageUploadRef.current.hasFile && imageUploadRef.current.hasFile()) {
                try {
                    const uploadedUrl = await imageUploadRef.current.uploadIfPending();
                    if (uploadedUrl) imageUrl = uploadedUrl;
                } catch (uErr) {
                    console.error('Image upload failed:', uErr);
                    toast.error('Image upload failed. Please try again.');
                    setSubmitting(false);
                    return;
                }
            }

            // Build services payload from selected ids (only verified services)
            const servicesPayload = selectedServiceIds
                .map((id) => servicesAvailable.find((s) => s.id === id))
                .filter(Boolean)
                .map((svc) => ({
                    id: svc.id,
                    imageUrl: svc.imageUrl || svc.image || null,
                    name: svc.name || svc.title || '',
                    price: svc.price || svc.basePrice || 0,
                    description: svc.description || '',
                }));

            // Calculate basePrice as the total of all selected services
            const basePrice = servicesPayload.reduce((sum, svc) => sum + (svc.price || 0), 0);

            if (editingEvent) {
                // If category changed, create in new category and delete old (move)
                if (formData.eventTypeId && formData.eventTypeId !== editingEvent.eventTypeId) {
                    const confirmed = window.confirm(
                        'Change category: this will move the event to the selected category (it creates a copy in the new category and deletes the old one). Proceed?'
                    );
                    if (!confirmed) { setSubmitting(false); return; }

                    // create in new category
                    const created = await createSubDoc('eventtypes', formData.eventTypeId, 'subevents', {
                        title: formData.title,
                        description: formData.description,
                        imageUrl: imageUrl || null,
                        basePrice,
                        isAvailable: !!formData.isAvailable,
                        services: servicesPayload,
                        rating: editingEvent.rating || 0,
                        reviewCount: editingEvent.reviewCount || 0,
                        created_at: editingEvent.created_at || editingEvent.createdAt || new Date().toISOString(),
                    });

                    try {
                        await updateSubDoc('eventtypes', formData.eventTypeId, 'subevents', created.id, { id: created.id, event_type_id: formData.eventTypeId });
                    } catch (uErr) {
                        console.warn('Failed to write id/event_type_id to moved event:', uErr);
                    }

                    // delete old
                    try {
                        await deleteSubDoc('eventtypes', editingEvent.eventTypeId, 'subevents', editingEvent.id);
                    } catch (delErr) {
                        console.error('Failed to delete old event after moving:', delErr);
                        toast.error('Moved event but failed to remove the original. Please remove it manually.');
                        handleCloseModal();
                        fetchEvents();
                        return;
                    }

                    toast.success('Event moved successfully');
                } else {
                    // normal update within same category
                    await updateSubDoc('eventtypes', editingEvent.eventTypeId, 'subevents', editingEvent.id, {
                        title: formData.title,
                        description: formData.description,
                        imageUrl: imageUrl || null,
                        basePrice,
                        isAvailable: !!formData.isAvailable,
                        services: servicesPayload,
                        id: editingEvent.id,
                    });
                    toast.success('Event updated successfully');
                }
            } else {
                // create subEvent under selected eventType
                if (!formData.eventTypeId) {
                    toast.error('Please select a category (event type)');
                    setSubmitting(false);
                    return;
                }

                const created = await createSubDoc('eventtypes', formData.eventTypeId, 'subevents', {
                    title: formData.title,
                    description: formData.description,
                    imageUrl: imageUrl || null,
                    basePrice,
                    isAvailable: !!formData.isAvailable,
                    services: servicesPayload,
                    rating: 0,
                    reviewCount: 0,
                    created_at: new Date().toISOString(),
                });

                // ensure the document contains its id and eventTypeId as fields (helpful for queries)
                try {
                    await updateSubDoc('eventtypes', formData.eventTypeId, 'subevents', created.id, { id: created.id, eventTypeId: formData.eventTypeId });
                } catch (uErr) {
                    // non-fatal, document was created - still proceed
                    console.warn('Failed to write id/eventTypeId into subEvent:', uErr);
                }

                toast.success('Event created successfully');
            }
            handleCloseModal();
            fetchEvents();
        } catch (err) {
            console.error(err);
            toast.error('Failed to save event');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (event) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                await deleteSubDoc('eventtypes', event.eventTypeId, 'subevents', event.id);
                toast.success('Event deleted successfully');
                fetchEvents();
            } catch (err) {
                console.error(err);
                toast.error('Failed to delete event');
            }
        }
    };

    if (loading) {
        return (
            <div className="events-container">
                <p>Loading events...</p>
            </div>
        );
    }

    return (
        <div className="events-container animate-fade-in">
            <div className="events-header">
                <h1>Events Management</h1>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    <FiPlus /> Add Event
                </button>
            </div>

            <div className="events-table-wrapper">
                <table className="events-table">
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Available</th>
                            <th>Description</th>
                            <th>Services</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.length > 0 ? (
                            events.map((event) => (
                                <tr key={event.id || event._id}>
                                    <td>
                                        {event.imageUrl ? (
                                            <img src={event.imageUrl} alt={event.title} style={{ width: 80, height: 60, objectFit: 'cover' }} />
                                        ) : (
                                            <div style={{ width: 80, height: 60, background: '#eee' }} />
                                        )}
                                    </td>
                                    <td>{event.title}</td>
                                    <td>{event.eventTypeTitle || 'N/A'}</td>
                                    <td>{event.basePrice ? `${event.basePrice} ETB` : '—'}</td>
                                    <td>{event.isAvailable ? 'Yes' : 'No'}</td>
                                    <td>{event.description || 'N/A'}</td>
                                    <td>{event.services && event.services.length ? `${event.services.length}` : '0'}</td>
                                    <td className="action-buttons">
                                        <button className="btn btn-sm btn-info" onClick={() => handleOpenModal(event)}>
                                            <FiEdit2 /> Edit
                                        </button>
                                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(event)}>
                                            <FiTrash2 /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-center">
                                    No events found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingEvent ? 'Edit Event' : 'Add New Event'}</h2>
                            <button className="modal-close" onClick={handleCloseModal}>
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="event-form">
                            <div className="form-group">
                                <label>Title *</label>
                                <input type="text" name="title" value={formData.title} onChange={handleInputChange} required />
                            </div>

                            <div className="form-group">
                                <label>Description *</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} required rows="3" />
                            </div>

                            <div className="form-group">
                                <label>Category *</label>
                                <select name="eventTypeId" value={formData.eventTypeId} onChange={handleInputChange} required>
                                    <option value="">Select Category</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id || cat._id} value={cat.id || cat._id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                                {editingEvent && (
                                    <small style={{ color: '#666' }}>
                                        Changing the category will move this event to the selected category on save (it will create a copy in the new
                                        category and remove it from the current one).
                                    </small>
                                )}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Available</label>
                                    <input type="checkbox" name="isAvailable" checked={!!formData.isAvailable} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>Image</label>
                                    <ImageUpload
                                        ref={imageUploadRef}
                                        initialUrl={formData.imageUrl}
                                        autoUpload={true}
                                        path={`eventTypes/${formData.eventTypeId || 'eventTypes'}`}
                                        onUpload={(url) => setFormData((prev) => ({ ...prev, imageUrl: url }))}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label>Attach Services</label>
                                    <button type="button" className="btn btn-sm" onClick={() => setShowAddService((s) => !s)}>
                                        {showAddService ? 'Cancel' : 'Add Service'}
                                    </button>
                                </div>

                                {servicesAvailable.length > 0 && selectedServiceIds.length > 0 && (
                                    <div style={{ padding: '8px', backgroundColor: '#f0f8ff', borderRadius: 4, marginTop: 8, marginBottom: 8 }}>
                                        <strong>Total Price (from services): {selectedServiceIds
                                            .map((id) => servicesAvailable.find((s) => s.id === id))
                                            .filter(Boolean)
                                            .reduce((sum, svc) => sum + (svc.price || 0), 0)} ETB</strong>
                                    </div>
                                )}

                                {showAddService && (
                                    <div style={{ border: '1px solid #eee', padding: 8, marginTop: 8, borderRadius: 4 }}>
                                        <div className="form-group">
                                            <label>Service Name</label>
                                            <input
                                                type="text"
                                                value={newServiceData.name}
                                                onChange={(e) => setNewServiceData((p) => ({ ...p, name: e.target.value }))}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Description</label>
                                            <input
                                                type="text"
                                                value={newServiceData.description}
                                                onChange={(e) => setNewServiceData((p) => ({ ...p, description: e.target.value }))}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Price</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={newServiceData.price}
                                                onChange={(e) => setNewServiceData((p) => ({ ...p, price: e.target.value }))}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Image</label>
                                            <ImageUpload ref={serviceImageRef} autoUpload={true} onUpload={(url) => setNewServiceData((p) => ({ ...p, imageUrl: url }))} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={async () => {
                                                    // create service inline
                                                    if (!newServiceData.name) return toast.error('Service name is required');
                                                    try {
                                                        setCreatingService(true);
                                                        // ensure pending upload is finished
                                                        if (serviceImageRef.current && serviceImageRef.current.hasFile && serviceImageRef.current.hasFile()) {
                                                            await serviceImageRef.current.uploadIfPending();
                                                        }
                                                        const created = await createDoc('services', {
                                                            name: newServiceData.name,
                                                            description: newServiceData.description || '',
                                                            price: parseFloat(newServiceData.price) || 0,
                                                            imageUrl: newServiceData.imageUrl || null,
                                                            created_at: new Date().toISOString(),
                                                        });
                                                        // write id into service doc (non-fatal)
                                                        try {
                                                            await updateDocById('services', created.id, { id: created.id });
                                                        } catch (inner) {
                                                            // ignore - we'll re-fetch the list
                                                        }
                                                        // refresh services list and auto-select the new one
                                                        await fetchServices();
                                                        setSelectedServiceIds((p) => [...p, created.id]);
                                                        setNewServiceData({ name: '', description: '', price: '' });
                                                        setShowAddService(false);
                                                        toast.success('Service created and attached');
                                                    } catch (err) {
                                                        console.error('Create service inline failed', err);
                                                        toast.error('Failed to create service');
                                                    } finally {
                                                        setCreatingService(false);
                                                    }
                                                }}
                                            >
                                                {creatingService ? 'Creating...' : 'Create & Attach'}
                                            </button>
                                            <button type="button" className="btn btn-secondary" onClick={() => setShowAddService(false)}>
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #eee', padding: 8, borderRadius: 4, marginTop: 8 }}>
                                    {servicesAvailable.filter((s) => s.status === 'verified').length > 0 ? (
                                        servicesAvailable
                                            .filter((s) => s.status === 'verified')
                                            .map((svc) => {
                                                const checked = selectedServiceIds.includes(svc.id);
                                                return (
                                                    <label key={svc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                if (e.target.checked) setSelectedServiceIds((prev) => [...prev, svc.id]);
                                                                else setSelectedServiceIds((prev) => prev.filter((id) => id !== svc.id));
                                                            }}
                                                        />
                                                        <img src={svc.imageUrl || svc.image || ''} alt={svc.name || svc.title} style={{ width: 40, height: 30, objectFit: 'cover' }} />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: 14, fontWeight: 600 }}>{svc.name || svc.title || 'Untitled'}</div>
                                                            <div style={{ fontSize: 12, color: '#666' }}>{svc.description || ''}</div>
                                                        </div>
                                                        <div style={{ fontWeight: 600 }}>{svc.price || svc.basePrice ? `${svc.price || svc.basePrice} ETB` : '—'}</div>
                                                    </label>
                                                );
                                            })
                                    ) : (
                                        <div style={{ color: '#666' }}>No verified services available. Verify services first before attaching them to an event.</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{
                                                width: 16, height: 16,
                                                border: '2px solid rgba(255,255,255,0.4)',
                                                borderTopColor: '#fff',
                                                borderRadius: '50%',
                                                display: 'inline-block',
                                                animation: 'spin 0.7s linear infinite',
                                            }} />
                                            {editingEvent ? 'Updating...' : 'Creating...'}
                                        </span>
                                    ) : (
                                        <>{editingEvent ? 'Update' : 'Create'} Event</>
                                    )}
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={submitting}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Events;